import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Search, X, AlertTriangle, AlertOctagon, CheckCircle2, ChevronDown, Copy, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Segmented } from '@/components/ui/segmented';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import {
  searchDrugs, getDrugDetails, getDrugPedsDosing, getDoctorDose, saveDoctorDose, saveDrugStrengthOverride,
  type DrugSearchResult, type DrugDetails, type DrugMolecule, type PedsDosing, type DoctorDose,
} from '@/lib/api';
import { hasUnsplittableSuffix } from '@/lib/constants';
import { parsePackVolumeMl } from '@/lib/dosage';
import { cn } from '@/lib/utils';
import { SyringeGauge, TabletGlyphs, DoseDay } from './DoseVisuals';
import {
  computeDose, matchRow, routeMatches, productKind, isSplittableForm, isDropsForm, normUnit, unitFamily,
  rowMode, rowDefaultRate, rowRangeLabel, rowBoundsLabel, typicalWeightForAge, formatNumber, formatAmount,
  FREQUENCIES, DOSE_UNITS, STRENGTH_UNITS, SOS_MIN_GAP_HOURS,
  type DoseMode, type Basis, type FrequencyKey, type Product, type ProductKind,
} from '@/lib/pedsDose';

export interface AppliedDose {
  drug: DrugDetails | null;       // null when calculated from a manually entered strength
  dosage: string;                 // "3.5ml-3.5ml-3.5ml"
  note: string;                   // dose basis kept with the medicine line: "15 mg/kg/dose · 12 kg"
  weightKg: number | null;        // weight used, so the prescription can fill empty vitals
  kind: ProductKind;
  givePerDose: number;            // ml or tablets per dose
  dosesPerDay: number;
  bottleMl: number | null;        // liquids: bottle size, for the quantity (bottles) calculation
}

interface PedsDoseCalculatorProps {
  initialDrugId?: number;
  initialWeightKg?: number;
  initialAgeMonths?: number;
  /** When provided (prescription drawer), the primary action is "Add to prescription"; otherwise "Copy dosage". */
  onApply?: (dose: AppliedDose) => void;
}

const FREQ_BY_PER_DAY: Record<number, FrequencyKey> = { 1: 'OD', 2: 'BD', 3: 'TDS', 4: 'QID', 6: 'Q4H' };
const CUSTOM_FORMS: { key: string; label: string; form: string }[] = [
  { key: 'syrup',   label: 'Syrup',   form: 'Syrup' },
  { key: 'drops',   label: 'Drops',   form: 'Drops' },
  { key: 'tablet',  label: 'Tablet',  form: 'Tablet' },
  { key: 'capsule', label: 'Capsule', form: 'Capsule' },
];
const MAX_VISIBLE_WARNINGS = 2;

// Plain decimals only — rejects "-", "e"/exponents and other input type="number" quirks
function onDecimalInput(value: string, set: (v: string) => void) {
  if (/^\d*\.?\d*$/.test(value)) set(value);
}

function Section({ label, children, className, order = 0 }: { label: string; children: ReactNode; className?: string; order?: number }) {
  return (
    <div className={cn('flex flex-col sm:flex-row sm:items-start gap-1.5 sm:gap-3 py-3.5 border-t border-border/70 first:border-t-0',
      // fill-mode-backwards, not -both: a transform kept after the animation makes each section a
      // stacking context, and the drug dropdown then renders underneath the sections below it
      'animate-in fade-in-0 slide-in-from-bottom-1 duration-300 fill-mode-backwards', className)}
      style={{ animationDelay: `${order * 45}ms` }}>
      <span className="sm:w-20 sm:pt-2 shrink-0 text-2xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      <div className="flex-1 min-w-0 space-y-2">{children}</div>
    </div>
  );
}

function Chip({ active, onClick, children, className }: { active?: boolean; onClick: () => void; children: ReactNode; className?: string }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
      className={cn('h-8 touch:min-h-10 px-3 rounded-lg text-xs font-medium border transition-colors',
        active ? 'border-primary bg-primary/10 text-primary' : 'border-border text-foreground hover:bg-muted/50', className)}>
      {children}
    </button>
  );
}

export default function PedsDoseCalculator({ initialDrugId, initialWeightKg, initialAgeMonths, onApply }: PedsDoseCalculatorProps) {
  const { currentUser } = useAuth();

  // ── Patient ──
  const [weight, setWeight]   = useState(initialWeightKg ? String(initialWeightKg) : '');
  const [age, setAge]         = useState(initialAgeMonths != null ? String(initialAgeMonths >= 24 ? Math.round(initialAgeMonths / 12 * 10) / 10 : initialAgeMonths) : '');
  const [ageUnit, setAgeUnit] = useState<'years' | 'months'>(initialAgeMonths != null && initialAgeMonths < 24 ? 'months' : 'years');

  // ── Dose ──
  const [mode, setMode]           = useState<DoseMode>('perKg');
  const [doseRate, setDoseRate]   = useState('');
  const [doseUnit, setDoseUnit]   = useState<string>('mg');
  const [basis, setBasis]         = useState<Basis>('dose');
  const [frequency, setFrequency] = useState<FrequencyKey>('TDS');

  // ── Drug ──
  const [query, setQuery]             = useState('');
  const [results, setResults]         = useState<DrugSearchResult[]>([]);
  const [searching, setSearching]     = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [selectedDrug, setSelectedDrug]               = useState<DrugDetails | null>(null);
  const [selectedMoleculeId, setSelectedMoleculeId]   = useState<number | null>(null);
  const [moleculeAutoSelected, setMoleculeAutoSelected] = useState(false);
  const [loadingDrug, setLoadingDrug] = useState(false);
  const [drugError, setDrugError]     = useState<string | null>(null);
  // No drug in the catalog: doctor enters the strength directly
  const [customForm, setCustomForm]   = useState<string | null>(null);

  // ── Strength (editable; session-only until phase 3 persists corrections) ──
  const [sAmount, setSAmount]         = useState('');
  const [sUnit, setSUnit]             = useState('mg');
  const [sPerVolume, setSPerVolume]   = useState('');
  const [catalogStrength, setCatalogStrength] = useState<{ amount: string; unit: string; perVolume: string } | null>(null);
  const [bottleMl, setBottleMl]       = useState('');

  // ── Reference doses ──
  const [pedsDosing, setPedsDosing]         = useState<PedsDosing[]>([]);
  const [loadingPeds, setLoadingPeds]       = useState(false);
  const [pedsFetchState, setPedsFetchState] = useState<'idle' | 'done' | 'error'>('idle');
  const [appliedRowId, setAppliedRowId]     = useState<number | null>(null);
  const [showAllRows, setShowAllRows]       = useState(false);
  // The doctor's own last-used dose for this ingredient — first suggestion, remembered on every use
  const [usualDose, setUsualDose]           = useState<DoctorDose | null>(null);
  const [usualApplied, setUsualApplied]     = useState(false);
  const [strengthSave, setStrengthSave]     = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // ── Result UI ──
  const [ackKey, setAckKey]                 = useState<string | null>(null);
  const [showWorking, setShowWorking]       = useState(false);
  const [showAllWarnings, setShowAllWarnings] = useState(false);
  const [copied, setCopied]                 = useState(false);

  const pedsRequestRef = useRef(0);
  const drugRequestRef = useRef(0);
  const doseRateRef    = useRef(doseRate);
  doseRateRef.current  = doseRate;
  const debounceRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchWrapRef  = useRef<HTMLDivElement>(null);
  const perVolumeRef   = useRef<HTMLInputElement>(null);

  // ── Search ──
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 3 || selectedDrug) { setResults([]); setSearching(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      searchDrugs(query, currentUser?.doctor_id || currentUser?.id || '')
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, selectedDrug, currentUser?.doctor_id, currentUser?.id]);

  useEffect(() => {
    function handleOutside(e: MouseEvent | TouchEvent) {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node)) setShowResults(false);
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, []);

  // ── Reference rows ──
  const applyRow = (row: PedsDosing) => {
    setAppliedRowId(row.id);
    setUsualApplied(false);
    setMode(rowMode(row));
    setDoseRate(formatNumber(rowDefaultRate(row)));
    const u = normUnit(row.dose_unit);
    if ((DOSE_UNITS as readonly string[]).includes(u)) setDoseUnit(u);
    setBasis(row.dose_basis === 'per_dose' ? 'dose' : 'day');
    const f = FREQ_BY_PER_DAY[row.doses_per_day];
    if (f) setFrequency(f);
  };

  const applyUsual = (u: DoctorDose) => {
    setAppliedRowId(null);
    setUsualApplied(true);
    setMode(u.dose_mode === 'fixed' ? 'fixed' : 'perKg');
    setDoseRate(formatNumber(Number(u.dose_value)));
    const unit = normUnit(u.dose_unit);
    if ((DOSE_UNITS as readonly string[]).includes(unit)) setDoseUnit(unit);
    setBasis(u.dose_basis === 'per_day' ? 'day' : 'dose');
    if (u.frequency && FREQUENCIES.some(f => f.key === u.frequency)) setFrequency(u.frequency as FrequencyKey);
  };

  const fetchPedsDosing = async (drug: DrugDetails, moleculeId: number) => {
    const requestId = ++pedsRequestRef.current;
    setLoadingPeds(true);
    try {
      const [rows, usual] = await Promise.all([
        getDrugPedsDosing(drug.id, moleculeId),
        getDoctorDose(moleculeId).catch(() => null),
      ]);
      if (requestId !== pedsRequestRef.current) return;
      setUsualDose(usual ?? null);
      // safeFetch resolves null on failure — treat that as an error, not "no data"
      if (!rows) throw new Error('peds dosing request failed');
      setPedsDosing(rows);
      setPedsFetchState('done');
      // Nothing typed yet → pre-fill: the doctor's usual first, else one unambiguous reference (top of range)
      const routed = rows.filter(r => routeMatches(r.route, drug.route));
      if (doseRateRef.current === '') {
        if (usual) applyUsual(usual);
        else if (routed.length === 1) applyRow(routed[0]);
      }
    } catch {
      if (requestId !== pedsRequestRef.current) return;
      setPedsDosing([]);
      setPedsFetchState('error');
    } finally {
      if (requestId === pedsRequestRef.current) setLoadingPeds(false);
    }
  };

  const loadStrength = (m: DrugMolecule | undefined) => {
    const s = {
      amount: m?.dose_value ? formatNumber(Number(m.dose_value)) : '',
      unit: normUnit(m?.dose_unit) || 'mg',
      perVolume: m?.per_volume_value ? formatNumber(Number(m.per_volume_value)) : '',
    };
    setSAmount(s.amount);
    setSUnit(s.unit);
    setSPerVolume(s.perVolume);
    setCatalogStrength(s);
  };

  const resetReference = () => {
    pedsRequestRef.current++;
    setPedsDosing([]);
    setPedsFetchState('idle');
    setLoadingPeds(false);
    setAppliedRowId(null);
    setShowAllRows(false);
    setUsualDose(null);
    setUsualApplied(false);
    setStrengthSave('idle');
  };

  const handleSelectDrug = async (drugId: number, brandName: string) => {
    if (loadingDrug) return;
    const requestId = ++drugRequestRef.current;
    setShowResults(false);
    setActiveIndex(-1);
    setQuery(brandName);
    setDrugError(null);
    setCustomForm(null);
    resetReference();
    setLoadingDrug(true);
    try {
      const details = await getDrugDetails(drugId).catch(() => null);
      if (requestId !== drugRequestRef.current) return;
      if (!details) {
        setQuery('');
        setDrugError(`Couldn't load "${brandName}". Try again.`);
        return;
      }
      setSelectedDrug(details);
      const pack = details.pack_volume_ml ? Number(details.pack_volume_ml) : parsePackVolumeMl(details.pack_size);
      setBottleMl(pack ? formatNumber(pack) : '');
      const molecules = details.molecules ?? [];
      const primary = molecules.find(m => m.is_primary);
      const autoSelected = !primary && molecules.length > 1;
      const molecule = primary ?? molecules[0];
      setMoleculeAutoSelected(autoSelected);
      setSelectedMoleculeId(molecule?.molecule_id ?? null);
      loadStrength(molecule);
      // Unambiguous ingredient only — reference doses for the wrong ingredient are unsafe
      if (molecule && !autoSelected) await fetchPedsDosing(details, molecule.molecule_id);
    } finally {
      if (requestId === drugRequestRef.current) setLoadingDrug(false);
    }
  };

  useEffect(() => {
    if (initialDrugId) handleSelectDrug(initialDrugId, '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDrugId]);

  const handleMoleculeChange = (moleculeId: number) => {
    if (!selectedDrug) return;
    setSelectedMoleculeId(moleculeId);
    setMoleculeAutoSelected(false);
    // A rate that came from the previous ingredient's reference doesn't apply to this one
    if (appliedRowId !== null) setDoseRate('');
    resetReference();
    loadStrength(selectedDrug.molecules.find(m => m.molecule_id === moleculeId));
    fetchPedsDosing(selectedDrug, moleculeId);
  };

  const clearDrug = () => {
    drugRequestRef.current++;
    // A dose filled in from this drug's suggestions must not carry over to the next drug
    if (appliedRowId !== null || usualApplied) setDoseRate('');
    resetReference();
    setSelectedDrug(null);
    setSelectedMoleculeId(null);
    setMoleculeAutoSelected(false);
    setLoadingDrug(false);
    setDrugError(null);
    setQuery('');
    setResults([]);
    setCustomForm(null);
    setCatalogStrength(null);
    setSAmount(''); setSUnit('mg'); setSPerVolume('');
  };

  // ── Derived ──
  const weightKg  = parseFloat(weight);
  const rate      = parseFloat(doseRate);
  const ageValue  = parseFloat(age);
  const ageMonths = ageValue >= 0 ? (ageUnit === 'years' ? ageValue * 12 : ageValue) : null;

  const molecules = selectedDrug?.molecules ?? [];
  const selectedMolecule = molecules.find(m => m.molecule_id === selectedMoleculeId);
  const customFormDef = CUSTOM_FORMS.find(f => f.key === customForm) ?? null;
  const form = selectedDrug?.form ?? customFormDef?.form ?? '';
  const kind: ProductKind = productKind(form, selectedMolecule?.strength_type);
  const product: Product | null = selectedDrug || customFormDef ? {
    form,
    kind,
    splittable: isSplittableForm(form) && !hasUnsplittableSuffix(selectedDrug?.brand_name ?? ''),
    strength: { amount: parseFloat(sAmount) || null, unit: sUnit, perVolume: parseFloat(sPerVolume) || null, perVolumeUnit: 'ml' },
  } : null;

  const routedDosing = pedsDosing.filter(r => routeMatches(r.route, selectedDrug?.route));
  const routeExcluded = pedsDosing.length > 0 && routedDosing.length === 0;
  const rowMatches = new Map(routedDosing.map(r => [r.id, matchRow(r, weightKg > 0 ? weightKg : null, ageMonths)]));
  const sortedRows = [...routedDosing].sort((a, b) => Number(rowMatches.get(b.id)!.inRange) - Number(rowMatches.get(a.id)!.inRange));
  const visibleRows = showAllRows ? sortedRows : sortedRows.slice(0, 3);

  // SOS limits come from the reference entry when it has them
  const sosRow = routedDosing.find(r => r.id === appliedRowId) ?? (routedDosing.length === 1 ? routedDosing[0] : null);
  const sosMaxPerDay = frequency === 'SOS' && sosRow?.max_doses_per_day ? Number(sosRow.max_doses_per_day) : undefined;
  const sosGapHours = sosRow?.min_interval_hours ? Number(sosRow.min_interval_hours) : SOS_MIN_GAP_HOURS;

  const result = computeDose({
    dosesPerDay: sosMaxPerDay,
    weightKg, ageMonths, mode, rate, rateUnit: doseUnit, basis, frequency, product,
    moleculeName: selectedMolecule?.molecule_name ?? null,
    coIngredients: molecules
      .filter(m => m.molecule_id !== selectedMoleculeId && m.dose_value && m.dose_unit)
      .map(m => ({ name: m.molecule_name, amount: Number(m.dose_value), unit: m.dose_unit!, perVolume: m.per_volume_value ? Number(m.per_volume_value) : null })),
    moleculeAutoSelected, rows: routedDosing, appliedRowId,
  });
  const { validInputs, give, pattern, delivered, needs, notes, referenceRow, family } = result;
  const errors   = notes.filter(n => n.level === 'error');
  const warnings = notes.filter(n => n.level === 'warning');
  const infos    = notes.filter(n => n.level === 'info');
  const errorKey = errors.map(e => e.text).join('|');
  const acknowledged = errors.length === 0 || ackKey === errorKey;
  const refComparable = referenceRow && rowMode(referenceRow) === mode && unitFamily(referenceRow.dose_unit) === family;

  // Different first-day dose (e.g. azithromycin 5-day course) — only while the inputs match that entry
  const loadingRow = referenceRow?.loading_dose_per_kg && rowMode(referenceRow) === mode
    && normUnit(referenceRow.dose_unit) === doseUnit
    && (referenceRow.dose_basis === 'per_dose' ? 'dose' : 'day') === basis ? referenceRow : null;
  const loadingResult = loadingRow && validInputs ? computeDose({
    weightKg, ageMonths, mode, rate: Number(loadingRow.loading_dose_per_kg), rateUnit: doseUnit, basis, frequency, product,
    moleculeName: null, coIngredients: [], moleculeAutoSelected: false, appliedRowId: -1,
    rows: [{
      ...loadingRow, id: -1, dose_per_kg: Number(loadingRow.loading_dose_per_kg), dose_per_kg_max: null,
      max_single_dose: loadingRow.loading_max_dose, max_daily_dose: loadingRow.loading_max_dose,
      min_age_months: null, max_age_months: null, min_weight_kg: null, max_weight_kg: null,
    }],
  }) : null;
  const loadingLabel = loadingResult
    ? (loadingResult.give ? loadingResult.give.display : formatAmount(loadingResult.perDose!, family!))
    : null;

  const strengthEdited = !!catalogStrength && (sAmount !== catalogStrength.amount || sUnit !== catalogStrength.unit || sPerVolume !== catalogStrength.perVolume);
  const strengthClinicCorrected = !!selectedMolecule?.strength_overridden && !strengthEdited;

  const saveStrengthForClinic = async () => {
    if (!selectedDrug || !selectedMolecule) return;
    setStrengthSave('saving');
    try {
      await saveDrugStrengthOverride(selectedDrug.id, {
        moleculeId: selectedMolecule.molecule_id,
        doseValue: parseFloat(sAmount) || null,
        doseUnit: sUnit,
        perVolumeValue: kind === 'liquid' ? parseFloat(sPerVolume) || null : null,
      });
      setCatalogStrength({ amount: sAmount, unit: sUnit, perVolume: sPerVolume });
      setStrengthSave('saved');
    } catch {
      setStrengthSave('error');
    }
  };
  const perVolumeMissingInCatalog = !!selectedDrug && kind === 'liquid' && !!catalogStrength && !catalogStrength.perVolume;
  const weightHint = !(weightKg > 0) && ageMonths != null ? typicalWeightForAge(ageMonths) : null;
  const unitSuffix = mode === 'perKg' ? `${doseUnit}/kg` : doseUnit;

  const primaryLabel = onApply ? 'Add to prescription' : 'Copy dosage';
  const rememberUse = () => {
    if (!selectedDrug || !selectedMolecule) return;
    saveDoctorDose(selectedMolecule.molecule_id, {
      doseMode: mode === 'fixed' ? 'fixed' : 'per_kg', doseValue: rate, doseUnit,
      doseBasis: basis === 'day' ? 'per_day' : 'per_dose', frequency,
    }).catch(() => { /* remembering is best-effort */ });
    const bottle = parseFloat(bottleMl);
    const knownBottle = selectedDrug.pack_volume_ml ? Number(selectedDrug.pack_volume_ml) : parsePackVolumeMl(selectedDrug.pack_size);
    if (onApply && kind === 'liquid' && bottle > 0 && bottle !== knownBottle) {
      saveDrugStrengthOverride(selectedDrug.id, { moleculeId: selectedMolecule.molecule_id, packVolumeMl: bottle }).catch(() => {});
    }
  };

  const handlePrimary = async () => {
    if (!pattern || !acknowledged) return;
    rememberUse();
    const dayOne = loadingLabel ? `Day 1: ${loadingLabel}, then ` : '';
    const note = `${dayOne}${formatNumber(rate)} ${unitSuffix}/${basis}${weightKg > 0 ? ` · ${weightKg} kg` : ''}`;
    if (onApply && give) {
      onApply({
        drug: selectedDrug, dosage: pattern, note,
        weightKg: weightKg > 0 ? weightKg : null,
        kind, givePerDose: give.value,
        dosesPerDay: sosMaxPerDay ?? FREQUENCIES.find(f => f.key === frequency)!.perDay,
        bottleMl: parseFloat(bottleMl) || null,
      });
    } else {
      try {
        await navigator.clipboard.writeText(pattern);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch { /* clipboard unavailable — the pattern is visible to copy manually */ }
    }
  };

  // ── Search keyboard ──
  const dropdownOpen = showResults && query.trim().length >= 3 && !searching && results.length > 0;
  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { setShowResults(false); setActiveIndex(-1); return; }
    if (!dropdownOpen) { if (e.key === 'ArrowDown') setShowResults(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => (i + 1) % results.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => (i <= 0 ? results.length - 1 : i - 1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = results[activeIndex >= 0 ? activeIndex : 0];
      if (pick) handleSelectDrug(pick.id, pick.brand_name);
    }
  };

  // ── Render ──
  const isDrawer = !!onApply;
  const perDayCount = sosMaxPerDay ?? FREQUENCIES.find(f => f.key === frequency)!.perDay;
  const tone = !validInputs ? 'idle' : errors.length ? 'error' : warnings.length ? 'warning' : refComparable ? 'ok' : 'neutral';
  const toneBar = { idle: 'bg-border', neutral: 'bg-border', ok: 'bg-success', warning: 'bg-warning', error: 'bg-destructive' }[tone];
  // "3.5 ml" → ["3.5", "ml"], "½ tablet" → ["½", "tablet"]
  const headline = validInputs ? (give ? give.display : formatAmount(result.perDose!, family!)) : '';
  const [headValue, ...headUnit] = headline.split(' ');
  const isCapsule = form.toLowerCase().includes('capsule');

  const primaryButton = (
    <Button type="button" size="sm" onClick={handlePrimary} disabled={!pattern || !acknowledged} className="gap-1.5"
      title={!pattern ? 'Complete the strength to get a dosage' : !acknowledged ? 'Review the red warning first' : undefined}>
      {!onApply && (copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />)}
      {copied ? 'Copied' : primaryLabel}
    </Button>
  );

  const inputs = (
    <div className="bg-card border border-border rounded-lg px-4 sm:px-5 py-2 [&_input::placeholder]:text-muted-foreground/35">

      {/* Drug */}
      <Section label="Drug">
        {selectedDrug ? (
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{selectedDrug.brand_name}</p>
                <p className="text-xs text-muted-foreground truncate">{[selectedMolecule?.molecule_name, selectedDrug.form].filter(Boolean).join(' · ')}</p>
              </div>
              <button type="button" onClick={clearDrug} aria-label="Clear drug" className="touch-target p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50">
                <X className="w-4 h-4" />
              </button>
            </div>
            {molecules.length > 1 && (
              <Select value={selectedMoleculeId !== null ? String(selectedMoleculeId) : ''} onValueChange={v => handleMoleculeChange(Number(v))}>
                <SelectTrigger className={cn('h-8 text-xs rounded-lg', moleculeAutoSelected && 'border-warning')}>
                  <SelectValue placeholder="Which ingredient is the dose for?" />
                </SelectTrigger>
                <SelectContent>
                  {molecules.map(m => <SelectItem key={m.molecule_id} value={String(m.molecule_id)}>Dose is for {m.molecule_name}{m.strength ? ` (${m.strength})` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        ) : customFormDef ? (
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-wrap gap-1.5">
              {CUSTOM_FORMS.map(f => <Chip key={f.key} active={customForm === f.key} onClick={() => setCustomForm(f.key)}>{f.label}</Chip>)}
            </div>
            <button type="button" onClick={() => { setCustomForm(null); setSAmount(''); setSPerVolume(''); }} className="text-xs text-primary hover:underline shrink-0">Search drug</button>
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="relative" ref={searchWrapRef}>
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                className="pl-9 h-9 text-sm rounded-lg"
                value={query}
                disabled={loadingDrug}
                role="combobox"
                aria-expanded={dropdownOpen}
                aria-controls="peds-drug-results"
                aria-activedescendant={dropdownOpen && activeIndex >= 0 ? `peds-drug-${results[activeIndex]?.id}` : undefined}
                onChange={e => { setQuery(e.target.value); setShowResults(true); setActiveIndex(-1); setDrugError(null); }}
                onFocus={() => setShowResults(true)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Calpol, Augmentin…"
              />
              {loadingDrug && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-2xs text-muted-foreground">Loading…</span>}
              {showResults && query.trim().length >= 3 && (
                <div id="peds-drug-results" role="listbox" className="absolute z-30 mt-1 w-full rounded-lg border border-border bg-popover shadow-lg max-h-64 overflow-y-auto">
                  {searching ? (
                    <p className="px-3 py-2.5 text-xs text-muted-foreground">Searching…</p>
                  ) : results.length > 0 ? results.map((d, i) => (
                    <button key={d.id} type="button" id={`peds-drug-${d.id}`} role="option" aria-selected={i === activeIndex}
                      className={cn('w-full text-left px-3 py-2 border-b border-border/40 last:border-0 hover:bg-muted', i === activeIndex && 'bg-muted')}
                      onMouseEnter={() => setActiveIndex(i)}
                      onMouseDown={() => handleSelectDrug(d.id, d.brand_name)}>
                      <span className="text-sm">{d.brand_name}</span>
                      <span className="ml-2 text-2xs text-muted-foreground">{d.form}</span>
                      {d.composition_raw && <p className="text-xs text-muted-foreground truncate">{d.composition_raw}</p>}
                    </button>
                  )) : (
                    <p className="px-3 py-2.5 text-xs text-muted-foreground">No drugs found</p>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-2 text-2xs">
              {showResults && query.trim().length > 0 && query.trim().length < 3
                ? <span className="text-muted-foreground">Type at least 3 characters</span>
                : <span />}
              <button type="button" onClick={() => { setCustomForm('syrup'); setShowResults(false); }} className="text-primary hover:underline">Not listed? Enter strength</button>
            </div>
            {drugError && <p className="text-xs text-destructive">{drugError}</p>}
          </div>
        )}
      </Section>

      {/* Patient */}
      <Section label="Patient" order={1}>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5">
            <Input inputMode="decimal" aria-label="Weight in kg" value={weight} onChange={e => onDecimalInput(e.target.value, setWeight)} placeholder="12.5" className="w-20 h-8 text-sm font-mono rounded-lg" />
            <span className="text-xs text-muted-foreground">kg</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Input inputMode="decimal" aria-label="Age" value={age} onChange={e => onDecimalInput(e.target.value, setAge)} placeholder={ageUnit === 'years' ? '2.5' : '4'} className="w-16 h-8 text-sm font-mono rounded-lg" />
            <Select value={ageUnit} onValueChange={v => setAgeUnit(v as 'years' | 'months')}>
              <SelectTrigger className="w-[84px] h-8 text-xs rounded-lg" aria-label="Age unit"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="years">years</SelectItem>
                <SelectItem value="months">months</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {weightHint && <p className="text-2xs text-muted-foreground">Typical at this age: {weightHint}</p>}
      </Section>

      {/* Strength — always editable; the catalog is often missing the "/5 ml" part */}
      {product && (
        <Section label="Strength" order={2}>
          <div className="flex flex-wrap items-center gap-1.5">
            <Input inputMode="decimal" aria-label="Strength amount" value={sAmount} onChange={e => onDecimalInput(e.target.value, setSAmount)} placeholder="250"
              className={cn('w-20 h-8 text-sm font-mono rounded-lg', needs === 'strength' && 'border-warning')} />
            <Select value={sUnit} onValueChange={setSUnit}>
              <SelectTrigger className="w-[72px] h-8 text-xs rounded-lg" aria-label="Strength unit"><SelectValue /></SelectTrigger>
              <SelectContent>{STRENGTH_UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
            {kind === 'liquid' ? (<>
              <span className="text-xs text-muted-foreground">per</span>
              <Input ref={perVolumeRef} inputMode="decimal" aria-label="Per ml" value={sPerVolume} onChange={e => onDecimalInput(e.target.value, setSPerVolume)}
                placeholder={isDropsForm(form) ? '1' : '5'}
                className={cn('w-14 h-8 text-sm font-mono rounded-lg', needs === 'perVolume' && 'border-warning ring-1 ring-warning/40')} />
              <span className="text-xs text-muted-foreground">ml</span>
              {!sPerVolume && (isDropsForm(form) ? ['1', '5'] : ['5', '1']).map((v, i) => (
                <Chip key={v} onClick={() => setSPerVolume(v)} className={cn('h-7 px-2', i === 0 && 'border-primary/50')}>/{v} ml</Chip>
              ))}
            </>) : kind === 'solid' ? (
              <span className="text-xs text-muted-foreground">per {product.splittable ? 'tablet' : `${form.toLowerCase().includes('capsule') ? 'capsule' : 'unit'} · not splittable`}</span>
            ) : null}
          </div>
          {perVolumeMissingInCatalog && !sPerVolume && <p className="text-2xs text-warning">Volume missing in catalog — enter it to get ml</p>}
          {strengthEdited && (
            <p className="text-2xs text-muted-foreground">
              Edited for this calculation
              {selectedDrug && selectedMolecule && (
                <> · <button type="button" onClick={saveStrengthForClinic} disabled={strengthSave === 'saving'} className="text-primary hover:underline">
                  {strengthSave === 'saving' ? 'Saving…' : 'Save for clinic'}
                </button></>
              )}
              {strengthSave === 'error' && <span className="text-destructive"> · couldn't save</span>}
            </p>
          )}
          {!strengthEdited && strengthSave === 'saved' && <p className="text-2xs text-success">Saved for your clinic</p>}
          {strengthClinicCorrected && strengthSave !== 'saved' && <p className="text-2xs text-muted-foreground">Corrected by your clinic</p>}
          {onApply && kind === 'liquid' && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-muted-foreground">Bottle</span>
              <Input inputMode="decimal" aria-label="Bottle size in ml" value={bottleMl} onChange={e => onDecimalInput(e.target.value, setBottleMl)} placeholder="60"
                className="w-16 h-8 text-sm font-mono rounded-lg" />
              <span className="text-xs text-muted-foreground">ml</span>
              {!bottleMl && ['30', '60', '100'].map(v => <Chip key={v} onClick={() => setBottleMl(v)} className="h-7 px-2">{v}</Chip>)}
              <span className="text-2xs text-muted-foreground">for quantity</span>
            </div>
          )}
        </Section>
      )}

      {/* Dose */}
      <Section label="Dose" order={3}>
        {selectedDrug && !moleculeAutoSelected && (
          <div className="space-y-1.5">
            {loadingPeds && routedDosing.length === 0 && <p className="text-2xs text-muted-foreground">Loading standard doses…</p>}
            {!loadingPeds && pedsFetchState === 'error' && <p className="text-2xs text-destructive">Couldn't load standard doses — enter your dose</p>}
            {!loadingPeds && pedsFetchState === 'done' && routedDosing.length === 0 && !usualDose && (
              <p className="text-2xs text-muted-foreground">
                {routeExcluded ? `No standard dose for the ${selectedDrug.route} route` : 'No standard dose on file'} — enter your dose
              </p>
            )}
            {(usualDose || visibleRows.length > 0) && (
              <div className="flex flex-wrap gap-1.5">
                {usualDose && (
                  <Chip active={usualApplied} onClick={() => applyUsual(usualDose)} className="h-auto py-1.5 text-left">
                    Your usual · {formatNumber(Number(usualDose.dose_value))} {normUnit(usualDose.dose_unit)}{usualDose.dose_mode === 'per_kg' ? '/kg' : ''}/{usualDose.dose_basis === 'per_day' ? 'day' : 'dose'}
                    {usualDose.frequency && <span className="ml-1 text-muted-foreground font-normal">· {FREQUENCIES.find(f => f.key === usualDose.frequency)?.label ?? usualDose.frequency}</span>}
                  </Chip>
                )}
                {visibleRows.map(row => {
                  const m = rowMatches.get(row.id)!;
                  return (
                    <Chip key={row.id} active={appliedRowId === row.id} onClick={() => applyRow(row)}
                      className={cn('h-auto py-1.5 text-left', !m.inRange && 'opacity-60')}>
                      <span>{row.source} {rowRangeLabel(row)}</span>
                      {row.indication && <span className="ml-1 text-muted-foreground font-normal">· {row.indication}</span>}
                      {m.age === false && <span className="ml-1 text-destructive font-normal">· not for age</span>}
                      {m.age !== false && m.weight === false && <span className="ml-1 text-warning font-normal">· other weight</span>}
                    </Chip>
                  );
                })}
                {sortedRows.length > 3 && !showAllRows && (
                  <button type="button" onClick={() => setShowAllRows(true)} className="text-2xs text-primary hover:underline">+{sortedRows.length - 3} more</button>
                )}
              </div>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          <Input inputMode="decimal" aria-label="Dose" value={doseRate} onChange={e => onDecimalInput(e.target.value, setDoseRate)} placeholder={mode === 'perKg' ? '15' : '20'}
            className="w-20 h-8 text-sm font-mono rounded-lg" />
          <Select value={doseUnit} onValueChange={setDoseUnit}>
            <SelectTrigger className="w-[72px] h-8 text-xs rounded-lg" aria-label="Dose unit"><SelectValue /></SelectTrigger>
            <SelectContent>{DOSE_UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select>
          <Segmented<DoseMode> aria-label="Dose by weight or fixed" value={mode} onChange={setMode}
            options={[{ value: 'perKg', label: 'per kg' }, { value: 'fixed', label: 'fixed' }]} />
          <Segmented<Basis> aria-label="Dose per dose or per day" value={basis} onChange={setBasis}
            options={[{ value: 'dose', label: 'per dose' }, { value: 'day', label: 'per day' }]} />
        </div>
        {referenceRow?.special_notes && <p className="text-2xs text-muted-foreground">{referenceRow.special_notes}</p>}
      </Section>

      {/* How often */}
      <Section label="How often" order={4}>
        <div className="flex flex-wrap gap-1.5">
          {FREQUENCIES.map(f => <Chip key={f.key} active={frequency === f.key} onClick={() => setFrequency(f.key)}>{f.label}</Chip>)}
        </div>
      </Section>

    </div>
  );

  // ── Result: a measuring instrument, not a paragraph ──
  const instrument = (
    <section aria-live="polite" aria-label="Dose result"
      className={cn('relative overflow-hidden rounded-lg border border-border bg-card pl-5 pr-4 py-4 space-y-3',
        !isDrawer && 'lg:sticky lg:top-4')}>
      <span aria-hidden="true" className={cn('absolute inset-y-0 left-0 w-1 transition-colors duration-300', toneBar)} />
      <p className="text-2xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Give per dose</p>

      {!validInputs ? (<>
        <p className="text-4xl font-semibold tracking-tight text-muted-foreground/25">—</p>
        <div className="opacity-40"><SyringeGauge ml={0} idle /></div>
        <p className="text-xs text-muted-foreground">{mode === 'perKg' && !(weightKg > 0) ? 'Enter weight and dose' : 'Enter the dose'}</p>
      </>) : (<>
        <div className="space-y-1.5">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span key={headline} className="text-[2.75rem] leading-none font-semibold tabular-nums tracking-tight animate-in fade-in-0 slide-in-from-bottom-1 duration-300">
              {headValue}
            </span>
            <span className="text-lg text-muted-foreground">{headUnit.join(' ')}</span>
          </div>
          {pattern && (
            <p><span className="inline-block rounded-md bg-muted px-2 py-0.5 font-mono text-sm tracking-tight">{pattern}</span></p>
          )}
        </div>

        {give && kind === 'liquid' && <SyringeGauge ml={give.value} />}
        {give && kind === 'solid' && <TabletGlyphs units={give.value} capsule={isCapsule} />}
        <DoseDay frequency={frequency} gapHours={sosGapHours} maxPerDay={perDayCount} />

        <div className="space-y-1 text-xs text-muted-foreground">
          <p>
            {give && delivered
              ? <>{formatAmount(delivered.perDose, family!)} per dose{mode === 'perKg' && delivered.perKgPerDose != null && <> · <span className="text-foreground tabular-nums">{formatNumber(basis === 'dose' ? delivered.perKgPerDose : delivered.perKgPerDay!)} {doseUnit}/kg/{basis}</span> given</>}</>
              : needs === 'perVolume' ? <button type="button" className="text-warning hover:underline" onClick={() => perVolumeRef.current?.focus()}>Enter ml per {sAmount || '…'} {sUnit} to get ml</button>
              : needs === 'strength' ? 'Enter the strength to get ml / tablets'
              : !product ? 'Select a drug for ml / tablets'
              : 'per dose'}
          </p>
          {loadingLabel && (
            <p className="font-medium text-foreground">Day 1: {loadingLabel}, then {give ? give.display : formatAmount(result.perDose!, family!)} {basis === 'day' && frequency === 'OD' ? 'daily' : 'per dose'}</p>
          )}
          {result.coIngredients.length > 0 && (
            <p>+ {result.coIngredients.map(c => `${formatAmount(c.perDose, c.family)} ${c.name}`).join(', ')} per dose</p>
          )}
        </div>

        {/* Status — red needs acknowledgement, amber is one line each, green when checked against a reference */}
        {errors.length > 0 && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 space-y-1">
            {errors.map(n => (
              <p key={n.text} className="flex items-start gap-1.5 text-xs text-destructive"><AlertOctagon className="w-3.5 h-3.5 mt-px shrink-0" />{n.text}</p>
            ))}
            {!acknowledged
              ? <button type="button" onClick={() => setAckKey(errorKey)} className="text-xs font-medium text-destructive underline underline-offset-2">I've checked this dose</button>
              : <p className="text-2xs text-muted-foreground">Checked by you</p>}
          </div>
        )}
        {warnings.length > 0 && (
          <div className="space-y-1">
            {(showAllWarnings ? warnings : warnings.slice(0, MAX_VISIBLE_WARNINGS)).map(n => (
              <p key={n.text} className="flex items-start gap-1.5 text-xs text-warning"><AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />{n.text}</p>
            ))}
            {warnings.length > MAX_VISIBLE_WARNINGS && !showAllWarnings && (
              <button type="button" onClick={() => setShowAllWarnings(true)} className="text-2xs text-muted-foreground hover:underline">+{warnings.length - MAX_VISIBLE_WARNINGS} more</button>
            )}
          </div>
        )}
        {errors.length === 0 && warnings.length === 0 && (
          refComparable
            ? <p className="flex items-center gap-1.5 text-xs text-success"><CheckCircle2 className="w-3.5 h-3.5" />Within {referenceRow!.source} range{result.capped ? '' : ' and max dose'}</p>
            : selectedDrug && mode === 'perKg' && pedsFetchState === 'done' && routedDosing.length === 0
            ? <p className="text-xs text-muted-foreground">No reference on file — check against your source</p>
            : null
        )}
      </>)}

      <div className="flex items-center justify-between gap-2 pt-1 border-t border-border/70">
        <button type="button" onClick={() => setShowWorking(v => !v)} aria-expanded={showWorking}
          className="flex items-center gap-1 pt-2 touch:min-h-10 text-xs text-muted-foreground hover:text-foreground">
          <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', showWorking && 'rotate-180')} /> Show working
        </button>
        {!isDrawer && <div className="pt-2">{primaryButton}</div>}
      </div>

      {showWorking && validInputs && (
        <div className="font-mono text-2xs text-muted-foreground space-y-0.5 animate-in fade-in-0 duration-200">
          {mode === 'perKg'
            ? <p>{weightKg} kg × {formatNumber(rate)} {unitSuffix}/{basis} = {formatAmount(basis === 'dose' ? result.rawPerDose! : result.rawDaily!, family!)}/{basis}</p>
            : <p>{formatNumber(rate)} {doseUnit}/{basis}</p>}
          {basis === 'day'
            ? <p>÷ {perDayCount} doses = {formatAmount(result.rawPerDose!, family!)}/dose</p>
            : <p>× {perDayCount} doses = {formatAmount(result.rawDaily!, family!)}/day</p>}
          {result.capped && <p className="text-warning">capped → {formatAmount(result.perDose!, family!)}/dose, {formatAmount(result.daily!, family!)}/day</p>}
          {give && product?.strength.amount && (
            <p>
              {formatAmount(result.perDose!, family!)} ÷ {formatNumber(product.strength.amount)} {normUnit(product.strength.unit)}
              {kind === 'liquid' ? `/${formatNumber(product.strength.perVolume!)} ml` : ''} = {formatNumber(give.exact)} → {give.display}
            </p>
          )}
          {referenceRow && (
            <p>ref: {referenceRow.source} {rowRangeLabel(referenceRow)}{referenceRow.indication ? ` (${referenceRow.indication})` : ''}{rowBoundsLabel(referenceRow) ? ` · ${rowBoundsLabel(referenceRow)}` : ''}</p>
          )}
          {infos.map(n => <p key={n.text}>{n.text}</p>)}
        </div>
      )}
    </section>
  );

  if (isDrawer) {
    // Drawer: everything stacks; the action bar stays pinned so "Add to prescription" never scrolls away
    return (
      <div className="space-y-3">
        {inputs}
        {instrument}
        {/* -bottom-4 / -mb-4 cover the sheet's p-4 so nothing scrolls visibly underneath the bar */}
        <div className="sticky -bottom-4 -mx-4 -mb-4 px-4 pt-3 pb-4 bg-background/95 backdrop-blur border-t border-border flex items-center justify-between gap-3">
          <p className="min-w-0 truncate text-sm">
            {pattern
              ? <><span className="font-semibold tabular-nums">{headline}</span> <span className="font-mono text-xs text-muted-foreground">{pattern}</span></>
              : <span className="text-muted-foreground text-xs">Complete the fields above</span>}
          </p>
          {primaryButton}
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,360px)] items-start">
      {inputs}
      {instrument}
    </div>
  );
}
